import type { AppliedNewEntity, AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import type { SpawnRowPlan } from '../janus-engine/spawn/row-plan';
import { refuseUnported } from '../janus-engine/unported';

// The outbox owns immutable JSON save bytes. Frame work runs only when a sender
// requests it; planning and local apply only retain the page's existing inputs.
const entities = new Map<string, Array<SpawnRowPlan | undefined>>();
const frames = new WeakMap<object, () => AppliedFrameResult>();
const computedDeltas = new WeakMap<object, import('../janus-engine/compile/action-computed-delta').ActionComputedDelta>();
export function exposeAppliedComputed(command: object, delta: import('../janus-engine/compile/action-computed-delta').ActionComputedDelta | undefined): void {
    if (delta) computedDeltas.set(command, delta);
}
export function stageAppliedEntities(operationId: string, plans: Array<SpawnRowPlan | undefined>): void {
    if (plans.some(Boolean)) entities.set(operationId, plans);
    if (entities.size > 256) entities.delete(entities.keys().next().value!);
}
export function takeAppliedEntities(operationId: string): () => readonly AppliedNewEntity[] {
    const plans = entities.get(operationId) ?? [];
    entities.delete(operationId);
    return () => {
        for (const plan of plans) if (plan) {
            try { void plan.new_entities; void plan.new_inventory; }
            catch (error) {
                if (!plan.defer_new_entities) throw error;
                const reason = plan.defer_new_entities(error);
                if (reason) refuseUnported(reason, { operation: 'spawn:pending-recovery' });
            }
        }
        return Object.defineProperty(plans.flatMap(plan => plan?.new_entities ?? []), 'new_inventory',
            { value: plans.flatMap(plan => plan?.new_inventory ?? []) });
    };
}
export function exposeAppliedFrame(command: object, build: () => AppliedFrameResult,
    project?: (frame: AppliedFrameResult) => AppliedFrameResult): void {
    let frame: AppliedFrameResult | undefined;
    frames.set(command, () => {
        if (!frame) {
            const built = build(), delta = computedDeltas.get(command);
            const observed = delta && !('cache_ref' in built) ? { ...built, computed_delta: delta } : built;
            // Derived inventory writes extend the observed delta and its before witnesses.
            frame = project ? project(observed) : observed;
        }
        return frame;
    });
}
export function appliedFrameOf(command: object): AppliedFrameResult | undefined { return frames.get(command)?.(); }
export function discardAppliedFrame(command: object): void { frames.delete(command); computedDeltas.delete(command); }
